/**
 * Dot-navigated testimonial slider used inside the satisfaction guarantee pop-up.
 * Kept self-contained (rather than reusing 'carousel-slider') because the slider
 * lives inside a modal that is off-screen until opened, and it navigates by dots
 * rather than prev/next buttons.
 */
class ExpertTrustSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;

    this.track = this.querySelector('.et-slider__track');
    this.slides = Array.from(this.querySelectorAll('.et-slide'));
    this.dots = Array.from(this.querySelectorAll('.et-dot'));
    if (!this.track || this.slides.length < 2) return;

    this.initialised = true;
    this.currentIndex = 0;
    this.autoplayDelay = Number(this.dataset.autoplay) * 1000;

    this.scrollHandler = this.handleScroll.bind(this);
    this.track.addEventListener('scroll', this.scrollHandler, { passive: true });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
        this.stopAutoplay();
      });
    });

    if (this.autoplayDelay > 0) {
      this.addEventListener('pointerdown', this.stopAutoplay.bind(this));
      this.addEventListener('mouseenter', this.pauseAutoplay.bind(this));
      this.addEventListener('mouseleave', this.startAutoplay.bind(this));
      this.addEventListener('focusin', this.pauseAutoplay.bind(this));

      // Only run autoplay while the containing modal is open.
      this.modal = this.closest('modal-dialog');

      if (this.modal) {
        this.observer = new MutationObserver(() => {
          if (this.modal.hasAttribute('open')) {
            this.startAutoplay();
          } else {
            this.pauseAutoplay();
          }
        });

        this.observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      } else {
        this.startAutoplay();
      }
    }
  }

  disconnectedCallback() {
    this.pauseAutoplay();
    if (this.observer) this.observer.disconnect();
    if (this.track) this.track.removeEventListener('scroll', this.scrollHandler);
  }

  /**
   * Syncs the dots with the slide currently scrolled into view.
   */
  handleScroll() {
    if (this.scrollTimer) clearTimeout(this.scrollTimer);

    this.scrollTimer = setTimeout(() => {
      const slideSpan = this.track.clientWidth;
      if (!slideSpan) return;

      const index = Math.round(Math.abs(this.track.scrollLeft) / slideSpan);
      this.setActive(Math.min(index, this.slides.length - 1));
    }, 80);
  }

  /**
   * Scrolls to a given slide.
   * @param {number} index - Zero-based slide index.
   */
  goTo(index) {
    const rtl = document.dir === 'rtl';
    const offset = this.track.clientWidth * index;

    this.track.scrollTo({ left: rtl ? -offset : offset, behavior: 'smooth' });
    this.setActive(index);
  }

  /**
   * Marks a dot as current.
   * @param {number} index - Zero-based slide index.
   */
  setActive(index) {
    if (index === this.currentIndex) return;

    this.currentIndex = index;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
  }

  startAutoplay() {
    if (this.autoplayStopped || !this.autoplayDelay) return;

    this.pauseAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.goTo((this.currentIndex + 1) % this.slides.length);
    }, this.autoplayDelay);
  }

  pauseAutoplay() {
    if (this.autoplayTimer) clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  /**
   * Stops autoplay for good, once the visitor takes control of the slider.
   */
  stopAutoplay() {
    this.autoplayStopped = true;
    this.pauseAutoplay();
  }
}

customElements.define('expert-trust-slider', ExpertTrustSlider);
